import React, { lazy, Suspense, useState } from 'react';
import { createPortal } from 'react-dom';
import { User } from '../types';
import { api } from '../services/apiService';
import { toast } from '../services/toast';

// Modale d'enrôlement chargée à la demande (qrcode + UI ne sert qu'à l'activation)
const TotpEnrollModal = lazy(() => import('./TotpEnrollModal'));

interface Props {
  user: User;
  onUserUpdate?: (u: User) => void;
}

const SecurityTab: React.FC<Props> = ({ user, onUserUpdate }) => {
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showDisablePrompt, setShowDisablePrompt] = useState(false);
  const [showRegenPrompt, setShowRegenPrompt] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [regenCodes, setRegenCodes] = useState<string[] | null>(null);

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      await api.totpDisable(password);
      toast.success('2FA désactivée');
      setShowDisablePrompt(false);
      setPassword('');
      onUserUpdate?.({ ...user, totpEnabled: false, totpEnrolledAt: null });
    } catch (err: any) {
      toast.error(err?.message || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      const { backupCodes } = await api.totpRegenerateBackupCodes(password);
      setRegenCodes(backupCodes);
      setPassword('');
      toast.success('Nouveaux codes générés');
    } catch (err: any) {
      toast.error(err?.message || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const handleEnrollSuccess = () => {
    setShowEnrollModal(false);
    onUserUpdate?.({ ...user, totpEnabled: true, totpEnrolledAt: new Date().toISOString() });
  };

  return (
    <div className="p-8 animate-in fade-in">
      <div className="mb-8">
        <h3 className="text-2xl font-extrabold text-[#e0eaff] mb-2 tracking-tight">Sécurité du compte</h3>
        <p className="text-[#546a96] text-sm font-medium">
          Protégez votre compte avec un second facteur d'authentification.
        </p>
      </div>

      <div className="rounded-2xl p-6 border border-[rgba(100,140,210,0.2)] bg-[rgba(100,140,210,0.06)]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-lg font-bold text-[#e0eaff]">Authentification à deux facteurs (2FA)</h4>
              {user.totpEnabled ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-[10px] font-black uppercase tracking-wider">
                  Activée
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-500/10 text-[#7f93bc] border border-[rgba(100,140,210,0.25)] text-[10px] font-black uppercase tracking-wider">
                  Inactive
                </span>
              )}
            </div>
            <p className="text-sm text-[#c8daf5] leading-relaxed">
              Ajoute une étape de vérification supplémentaire à chaque connexion via une application
              d'authentification (Google Authenticator, Microsoft Authenticator, Authy, 1Password,
              Bitwarden…).
            </p>
            {user.totpEnabled && user.totpEnrolledAt && (
              <p className="text-xs text-[#546a96] mt-2">
                Activée le {new Date(user.totpEnrolledAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {!user.totpEnabled ? (
              <button
                type="button"
                onClick={() => setShowEnrollModal(true)}
                className="orion-settings-primary-btn text-white font-bold px-5 py-2.5 rounded-xl text-sm shadow-lg"
              >
                Activer la 2FA
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => { setShowRegenPrompt(true); setRegenCodes(null); }}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold bg-violet-500/15 text-violet-300 border border-violet-500/30 hover:bg-violet-500/25 transition-all"
                >
                  Régénérer les codes de secours
                </button>
                <button
                  type="button"
                  onClick={() => setShowDisablePrompt(true)}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold bg-rose-500/15 text-rose-300 border border-rose-500/30 hover:bg-rose-500/25 transition-all"
                >
                  Désactiver la 2FA
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {!user.totpEnabled && (
        <div className="orion-warn-soft mt-6 rounded-xl p-4 text-sm leading-relaxed">
          <strong className="font-bold">Recommandation :</strong> activer la 2FA réduit
          fortement le risque de compromission de votre compte, même si votre mot de passe est volé.
          C'est rapide (~2 minutes) et compatible avec toutes les apps standards de 2FA.
        </div>
      )}

      {/* Modale d'enrôlement (lazy) */}
      {showEnrollModal && (
        <Suspense fallback={null}>
          <TotpEnrollModal
            userEmail={user.email}
            onClose={() => setShowEnrollModal(false)}
            onSuccess={handleEnrollSuccess}
          />
        </Suspense>
      )}

      {/* Confirmation désactivation */}
      {showDisablePrompt && createPortal(
        <div className="orion-modal-backdrop fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
          <form onSubmit={handleDisable} className="orion-modal-panel w-full max-w-md rounded-[2rem] shadow-2xl p-6 border">
            <h4 className="text-lg font-bold text-[#e0eaff] mb-2">Désactiver la 2FA</h4>
            <p className="text-sm text-[#c8daf5] mb-4">
              Confirmez avec votre mot de passe. Vos codes de secours seront supprimés.
            </p>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Mot de passe actuel"
              className="orion-settings-input w-full rounded-xl px-4 py-2.5 text-sm font-medium outline-none mb-4"
              autoFocus
              required
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setShowDisablePrompt(false); setPassword(''); }} className="px-4 py-2 rounded-xl text-sm font-bold text-[#c8daf5] hover:bg-[rgba(100,140,210,0.1)]">
                Annuler
              </button>
              <button type="submit" disabled={busy || !password} className="px-4 py-2 rounded-xl text-sm font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40 hover:bg-rose-500/30 disabled:opacity-50">
                {busy ? '…' : 'Désactiver'}
              </button>
            </div>
          </form>
        </div>,
        document.body
      )}

      {/* Confirmation régénération + affichage des nouveaux codes */}
      {showRegenPrompt && createPortal(
        <div className="orion-modal-backdrop fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="orion-modal-panel w-full max-w-md rounded-[2rem] shadow-2xl p-6 border">
            {!regenCodes ? (
              <form onSubmit={handleRegenerate}>
                <h4 className="text-lg font-bold text-[#e0eaff] mb-2">Régénérer les codes de secours</h4>
                <p className="text-sm text-[#c8daf5] mb-4">
                  Les anciens codes (utilisés ou non) seront invalidés. Confirmez avec votre mot de passe.
                </p>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Mot de passe actuel"
                  className="orion-settings-input w-full rounded-xl px-4 py-2.5 text-sm font-medium outline-none mb-4"
                  autoFocus
                  required
                />
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => { setShowRegenPrompt(false); setPassword(''); }} className="px-4 py-2 rounded-xl text-sm font-bold text-[#c8daf5] hover:bg-[rgba(100,140,210,0.1)]">
                    Annuler
                  </button>
                  <button type="submit" disabled={busy || !password} className="px-4 py-2 rounded-xl text-sm font-bold bg-violet-500/20 text-violet-300 border border-violet-500/40 hover:bg-violet-500/30 disabled:opacity-50">
                    {busy ? '…' : 'Régénérer'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <h4 className="text-lg font-bold text-[#e0eaff] mb-2">Nouveaux codes de secours</h4>
                <p className="text-sm text-[#c8daf5] mb-4">
                  Conservez ces codes en lieu sûr. Chacun ne peut servir qu'une seule fois.
                </p>
                <div className="grid grid-cols-2 gap-2 mb-4 font-mono text-sm">
                  {regenCodes.map(c => (
                    <div key={c} className="bg-[rgba(100,140,210,0.08)] border border-[rgba(100,140,210,0.25)] text-[#e0eaff] rounded-lg px-3 py-2 text-center">{c}</div>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(regenCodes.join('\n')).then(() => toast.info('Codes copiés'))}
                    className="px-4 py-2 rounded-xl text-sm font-bold bg-[rgba(100,140,210,0.12)] text-[#c8daf5] hover:bg-[rgba(100,140,210,0.2)]"
                  >
                    Copier
                  </button>
                  <button type="button" onClick={() => { setShowRegenPrompt(false); setRegenCodes(null); }} className="orion-settings-primary-btn text-white font-bold px-4 py-2 rounded-xl text-sm">
                    Fermer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SecurityTab;
